/** AI 生成内容 */
// 使用 vue 组件的方式混入，无需添加额外白名单混淆
// 可考虑使用模块导出代替mixin，提高代码可维护性
import { Vue, Component, Prop } from "vue-property-decorator";
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { urls } from "@/public/utils/env.js";
import { userMsgType, analyseModeMap } from "@/app/components/ai3/enum";

// 使用图片分析的mode参数列表，包括图片分析、流程图分析、甘特图分析、图表/表格分析、组织结构图分析、自由提问
const analyseModes = ["image", "text-analyse"];
analyseModeMap.forEach((value) => {
    analyseModes.push(value);
});

const i18nText = {
    zh: {
        emptyTheme: "主题文本不能为空",
        limitedWord: "指令中检测到敏感信息，无法帮您执行/(ㄒoㄒ)/~~",
        netError: "网络开小差了，请检查网络设置",
        "upload-image-fail": "图片上传失败",
        "image-size-error": "图片大小不能超过20M",
    },
    en: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "Please Check Your Network Connection.",
        "upload-image-fail": "Image upload failed.",
        "image-size-error": "The size of the image cannot exceed 20MB.",
    },
    fr: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "Erreur réseau, veuillez vérifier le réseau.",
        "upload-image-fail": "Le téléchargement de l'image a échoué.",
        "image-size-error": "La taille de l'image ne peut pas dépasser 20MB.",
    },
    de: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "Netzwerkfehler, bitte überprüfen Sie das Netzwerk",
        "upload-image-fail": "Bildupload fehlgeschlagen.",
        "image-size-error": "Die Größe des Bildes darf 20MB nicht überschreiten.",
    },
    jp: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "ネットワークエラー、ネットワークを確認してください。",
        "upload-image-fail": "画像のアップロードに失敗しました。",
        "image-size-error": "画像のサイズは20MBを超えることはできません。",
    },
    es: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "Error de red, por favor compruebe la red",
        "upload-image-fail": "La carga de la imagen ha fallado.",
        "image-size-error": "El tamaño de la imagen no puede exceder los 20MB.",
    },
    pt: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "Erro de Rede, por favor, verificar a rede.",
        "upload-image-fail": "Falha no upload da imagem.",
        "image-size-error": "O tamanho da imagem não pode exceder 20MB.",
    },
    it: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "Errore di rete. Si prega di controllare lo stato della connessione.",
        "upload-image-fail": "Caricamento dell'immagine non riuscito.",
        "image-size-error": "La dimensione dell'immagine non può superare i 20MB.",
    },
    tw: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "網絡錯誤，請檢查網絡",
        "upload-image-fail": "圖片上傳失敗。",
        "image-size-error": "圖片的大小不能超過20MB。",
    },
    kr: {
        emptyTheme: "Subject text cannot be empty",
        limitedWord: "Detect sensitive information in the command. The command cannot be executed.",
        netError: "네트워크 오류입니다. 네트워크를 확인하십시오.",
        "upload-image-fail": "이미지 업로드 실패.",
        "image-size-error": "이미지의 크기는 20MB를 초과할 수 없습니다.",
    },
};

@Component
export default class AiSocket extends Vue {
    from = "default";
    recg_kw_onceFlag = true;
    resultText: string = ""; // 生成的文本
    socket = null;
    sessionId: string = "";
    tempSessionId: string = ""; // 持续对话sessionid
    clientId: string = "";
    connId: string = "";
    inputText: string = "";
    commandMode: string = "chat";
    extraParams: object = null;
    aiPromiseRs = null; // promise resolve
    aiPromiseRj = null; // promise reject
    aiReceive = null;
    aiDone = false; // ws done
    msgCache = [];
    httpReqDone = false;
    linking = false;
    errorCode = 0;
    matchMode = null;
    imgDatas = [];

    get userInfo() {
        return this.$store.state.userInfo || this.$store.state.user.userInfo;
    }
    get socketChannel() {
        return `ai-completion-${this.userInfo.user_id}`;
    }
    get activeText() {
        return this.$store.state.shape.activeText;
    }
    get langText() {
        return i18nText[this.lang];
    }
    get commandText() {
        return this.inputText || this.activeText;
    }

    initWebSocket() {
        let message = ({ data }) => {
            if (data.startsWith("sub:/") && data.endsWith(":ok")) {
                // 发送指令
                this.sendCommand();
                this.aiDone = false;
            } else if (data.indexOf(":fail:") > -1) {
                // 取消订阅
                this.socketSend("uns");
                // this.excuteCommand();
            } else if (data.startsWith("uns:/") && data.endsWith(":ok")) {
                console.log("stop!");
                this.aiDone = true;
                this.activeText && window["_view"].AISelTool();
                this.aiPromiseRj(this.errorCode);
            } else {
                const res = JSON.parse(data);
                if (res.channel != this.socketChannel) return;
                this.msgCache.push(res);
                this.httpReqDone && this.linkCacheMsg();
            }
        };
        return new Promise((rs) => {
            let domain = urls.edrawmaxApi.replace("http", "ws");
            if (window.EDCONFIG.IS_SDK) {
                domain = urls.apiUrl.replace("http", "ws");
            }
            // let wsurl = `ws://dev.edrawmax.cn/ws/sub?client_id=${this.clientId}&conn_id=${this.connId}&token=${this.userInfo.token}`;
            let wsurl = `${domain}/ws/sub?client_id=${this.clientId}&conn_id=${this.connId}&token=${this.userInfo.token}`;
            this.socket = new WebSocket(wsurl);
            this.socket.onmessage = message;
            this.socket.onopen = () => rs();
            this.socket.onerror = () => {
                console.log("💔Socket error");
                this.initWebSocket();
            };
            this.socket.onclose = () => {
                console.log("💔Socket Closed💔");
            };
        });
    }

    linkCacheMsg() {
        if (this.linking) return;
        this.linking = true;
        while (this.msgCache.length != 0) {
            let res = this.msgCache.shift();
            if (res.msg.session_id == this.sessionId) {
                if (res.msg.finish_reason == "continue") {
                    this.resultText += res.msg.text;
                    this.aiReceive && this.aiReceive(res.msg.text);
                } else if (res.msg.finish_reason == "complete") {
                    this.txteVerify();
                    this.aiPromiseRs();
                    this.aiReceive = null;
                }
            }
        }
        this.linking = false;
    }
    // type: sub(订阅)，uns(取消订阅)
    socketSend(type) {
        let timeStamp = new Date().getTime();
        let data = `${type}:/${this.socketChannel}:${timeStamp}`;
        if (this.socket.readyState === this.socket.OPEN) {
            this.socket.send(data);
        }
    }
    excuteCommand() {
        if (!this.commandText && !this.extraParams["extra"] && !this.extraParams["prompt"] && this.commandMode !== "image") return;
        this.resultText = "";
        this.socketSend("sub");
    }
    sendCommand() {
        let url = `${urls.edrawmaxApi}/api/ai/v1/completions`;
        if (window.EDCONFIG.IS_SDK) {
            url = `${this.urls.apiUrl}/api/ai/v1/completions`;
        }
        let params: any = {
            prompt: this.commandText,
            mode: this.commandMode,
            user_lang: this.lang.replace("zh", "cn"),
            lang: this.lang.replace("zh", "cn"),
            platform: "web",
            version: this.appVersion,
            recg_kw: this.recg_kw_onceFlag && this.from == "rightPanel" && this.commandMode == "chat",
            img_datas: this.imgDatas,
        };

        if (params.img_datas?.length > 0) {
            params.img_method = "data";
        }

        this.matchMode = null;
        this.httpReqDone = false;
        Object.assign(params, this.extraParams);
        axios
            .post(url, params, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + this.userInfo.token,
                },
            })
            .then(({ data }) => {
                if (params["session_id"] == 1) {
                    this.tempSessionId = data.data.session_id;
                }
                if (data.data["match_modes"] && data.data["match_modes"].length > 0) {
                    this.matchMode = data.data["match_modes"][0];
                    this.aiPromiseRs();
                    this.socketSend("uns");
                }
                this.sessionId = data.data.session_id;
                this.httpReqDone = true;
            })
            .catch((e) => {
                /**
                 * http状态码  返回参数code  描述
                 * 400         400	        请求参数错误
                 * 401	       401	        鉴权失败
                 * 500	       500	        服务器异常
                 * 406	       2406	        触发风控
                 * 403	       3000	        权益额度用尽
                 * 403	       3001	        触发并发限制
                 * 403	       3002	        触发流控
                 */
                let code = e?.response?.data?.code || 0;
                this.errorCode = code;
                if (code == 2406) {
                    this.$edMessage.error(this.langText["limitedWord"]);
                } else if (code == 3000) {
                    // AI 点数充值弹窗
                    if (window.EDCONFIG.IS_SDK) {
                        this.sdkApp.promisePostMessage("SHOW_AI_POINTS_MODAL");
                    } else {
                        this.$store.commit("setStatus", {
                            type: "showAIPointTipsDialog",
                            data: true,
                        });
                    }
                } else {
                    this.$edMessage.error(this.langText["netError"]);
                }
                this.activeText && window["_view"].AISelTool();
                this.socketSend("uns");
            });
    }
    // 文本敏感检测
    txteVerify() {
        if (!this.internal) {
            this.activeText && window["_view"].AISelTool();
            this.socketSend("uns");
            return;
        }
        if (!this.resultText) return;
        let params = {
            text: this.resultText,
            uid: this.userInfo.user_id,
            token: this.userInfo.token,
        };
        this.$edApi
            .txtVerify(params)
            .then(({ data }) => {
                if (data.code != 0) return;

                const res = data.data;
                // 严格审核
                if (res.conclusionType != 1) {
                    res.wordHitPositions.forEach((w) => {
                        w.positions.forEach((p) => {
                            const str = this.resultText.substring(p[0], p[1] + 1);
                            this.resultText = this.resultText.replace(str, "*".repeat(str.length));
                        });
                    });
                }
            })
            .finally(() => {
                this.activeText && window["_view"].AISelTool();
                this.socketSend("uns");
            });
    }
    // 使用 fingerprintjs 同时生成两个唯一 id
    async generateFP() {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        const uniqueId = result.visitorId;
        return {
            clientId: "client_" + uniqueId,
            connId: "conn_" + uniqueId,
        };
    }
    // 组件执行方法
    generating(mode: string = "chat", params: object = {}, aiReceive = null) {
        this.sensors.track("clickAIGeneratedContent", {
            Catalogues: "AI",
        });
        this.aiReceive = aiReceive;
        return new Promise((resolve, reject) => {
            this.aiPromiseRs = resolve;
            this.aiPromiseRj = reject;
            if (this.commandText || params["extra"] || params["prompt"] || mode === "image") {
                this.commandMode = mode;
                if (params) {
                    this.extraParams = params;
                }
                this.excuteCommand();
                if (!analyseModes.includes(mode) && this.activeText) {
                    window["_view"].AISelTool(true);
                }
            } else {
                this.$edMessage.error(this.langText["emptyTheme"]);
                reject("emptyTheme");
            }
        });
    }
    // 测试
    generatingTest(mode: string = "chat", params: object = {}, aiReceive = null) {
        this.resultText = `mermaid
        timeline
        title 事件时间线
    
        section 事件1
        2022-01-01 : 事件1发生
    
        section 事件2
        2022-01-05 : 事件2发生
    
        section 事件3
        2022-01-10 : 事件3发生
    `;
        return Promise.resolve();
    }
    // 组件初始化方法
    initAiSocket() {
        console.log("AI mixin");
        return this.generateFP()
            .then((res) => {
                this.clientId = res.clientId;
                this.connId = res.connId;
                if (!this.socket) {
                    return this.initWebSocket();
                } else {
                    return Promise.resolve();
                }
            })
            .catch((e) => {
                console.log("fp fild");
            });
    }

    // 检查是否生成aisocket
    checkSocket() {
        return (this.socket && Promise.resolve()) || this.initAiSocket();
    }

    // 未登录状态请求
    generatingNologin(mode: string = "chat", params: object = {}, aiReceive = null) {
        // 暂不开启
        this.$bus.$emit("nologinSave");
        return Promise.reject();
        const url = `${urls.edrawmaxApi}/api/ai/v1/completions/na`;
        this.commandMode = mode;
        let obj = {
            prompt: this.commandText,
            mode: this.commandMode,
            user_lang: this.lang.replace("zh", "cn"),
            platform: "web",
            version: this.appVersion,
            recg_kw: this.recg_kw_onceFlag && this.from == "rightPanel" && this.commandMode == "chat",
            img_datas: this.imgDatas,
        };
        this.matchMode = null;
        this.httpReqDone = false;
        Object.assign(obj, params);
        this.activeText && window["_view"].AISelTool(true);
        return axios
            .post(url, obj)
            .then((res) => {
                this.resultText = res.data.data.content;
                this.activeText && window["_view"].AISelTool();
            })
            .catch((e) => {
                let code = e.response.data.code;
                this.errorCode = code;
                if (code == 2406) {
                    this.$edMessage.error(this.langText["limitedWord"]);
                } else {
                    this.$edMessage.error(this.langText["netError"]);
                }
                this.activeText && window["_view"].AISelTool();
            });
    }

    // 浮动工具栏-图片分析点击事件
    diagramAnalysisHandler(typeStr) {
        const view = window["_view"];
        const type = typeStr === "diagram-selected" ? 3 : 4;
        const maxSize = 20 * 1024 * 1024; // 20M

        view.exportToImage(
            100, // 只要是不缩放的，设置宽高不影响最后的图片大小
            100,
            type,
            0, // JPEG
            1, // 不缩放
            96,
            96,
            false, // 无水印
            false,
            false,
            [],
            async ({ size, blob }) => {
                const imageDataUrl = URL.createObjectURL(blob);

                if (size > maxSize) {
                    this.$edMessage.error(this.langText["image-size-error"]);
                    return;
                }

                // 打开AI工具栏，设置user消息为上传的图片
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onload = (e) => {
                    const src = e?.target?.result;
                    const img = new Image();
                    img.src = src as string;
                    img.onload = () => {
                        const wth = img.width / img.height;
                        const htw = img.height / img.width;
                        this.$bus.$emit("showAIPanel", true);
                        this.$nextTick(() => {
                            this.$bus.$emit("aiMsgEvent", {
                                action: "userMsgForPicture",
                                str: "",
                                msgType: userMsgType.picture,
                                pictures: [
                                    {
                                        url: imageDataUrl,
                                        htw,
                                        wth,
                                    },
                                ],
                            });

                            this.$bus.$emit("aiMsgEvent", {
                                action: "pictureTask",
                                str: "",
                                imgDatas: [
                                    {
                                        mime_type: "image/jpeg",
                                        data: (src as string)?.split(",")?.[1], // Base64
                                    },
                                ],
                            });
                        });
                    };
                };
            }
        );

        this.sensors.track("clickPictureAnalysisAI", {
            Catalogues: "AI",
            click_type: typeStr === "diagram-selected" ? "Analyze the selected part" : "Analyze the current page",
        });
    }
}
